import { queryOptions } from '@tanstack/vue-query';
import { type MaybeRef, toValue } from 'vue';

import { getSnapshot } from '@/entities/snapshot';

import { getFirewall } from '../firewall';
import { getVolume } from '../volume';
import {
  getDataForCreateStacklet,
  getStacklet,
  getStackletBackups,
  getStackletHistory,
  getStacklets,
  getStackletSnapshots,
} from './api';
import { stackletKeys } from './keys';

export const stackletDataForCreateQueryOptions = () =>
  queryOptions({
    queryKey: stackletKeys.dataForCreate(),
    queryFn: () => getDataForCreateStacklet(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

export const stackletQueryOptions = (id: MaybeRef<number>) => ({
  queryKey: stackletKeys.stacklet(id),
  queryFn: () => getStacklet(toValue(id)).then((data) => data.stacklet),
});

export const stackletsQueryOptions = (
  params: MaybeRef<{
    page: number;
    query?: string;
    status?: 'active' | 'inactive';
    hasBackups?: boolean;
  }>,
) => ({
  queryKey: stackletKeys.stacklets(params),
  queryFn: () => getStacklets(toValue(params)),
});

export const stackletTaskQueryOptions = (id: MaybeRef<number>) => ({
  queryKey: stackletKeys.stackletTask(id),
  queryFn: () => getStacklet(toValue(id)).then((data) => data.stacklet),
});

export const stackletBackupsQueryOptions = (id: MaybeRef<number>) => ({
  queryKey: stackletKeys.backups(id),
  queryFn: () => getStackletBackups(toValue(id)),
});

export const stackletHistoryQueryOptions = (
  id: MaybeRef<number>,
  params: MaybeRef<{ page?: number; query?: string }>,
) => ({
  queryKey: stackletKeys.history(id, params),
  queryFn: () => getStackletHistory(toValue(id), toValue(params)),
});

export const stackletSnapshotsQueryOptions = (id: MaybeRef<number>) => ({
  queryKey: stackletKeys.snapshots(id),
  queryFn: () => getStackletSnapshots(toValue(id)),
});

export const stackletSnapshotTaskQueryOptions = (id: MaybeRef<number>) => ({
  queryKey: stackletKeys.snapshotTask(id),
  queryFn: () => getSnapshot(toValue(id)),
});

export const stackletVolumeQueryOptions = (
  stackletId: MaybeRef<number>,
  volumeUuid: MaybeRef<string>,
) => ({
  queryKey: stackletKeys.volume(stackletId, volumeUuid),
  queryFn: () => getVolume(toValue(volumeUuid)).then((data) => data.volume),
});

export const stackletFirewallQueryOptions = (
  stackletId: MaybeRef<number>,
  firewallUuid: MaybeRef<string>,
) => ({
  queryKey: stackletKeys.firewall(stackletId, firewallUuid),
  queryFn: () => getFirewall(toValue(firewallUuid)).then((data) => data.firewall),
});
